import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';

import Imgix from 'shared/components/imgix';
import { DEFAULT_SPECIALS_CARD_IMG } from 'shared/constants';

type SpecialDetailHeaderProps = {
  displayImage?: string | null;
  displayName?: string;
  displayDescription?: string;
  showExpiredSpecial?: boolean;
};

export function SpecialDetailHeader({
  displayImage,
  displayName,
  displayDescription,
  showExpiredSpecial = false,
}: SpecialDetailHeaderProps): JSX.Element {
  const [imageLoaded, setImageLoaded] = useState(false);
  const imageUrl = displayImage || DEFAULT_SPECIALS_CARD_IMG;

  return (
    <Container $showExpiredSpecial={showExpiredSpecial} data-testid='special-detail-header'>
      <TextContainer>
        <Title>{displayName}</Title>
        {displayDescription && <Description>{displayDescription}</Description>}
      </TextContainer>
      <ImageContainer>
        {!imageLoaded && <ImageSkeleton data-testid='image-skeleton' />}
        <BannerImage
          src={imageUrl}
          disableSrcSet
          imgixParams={{ height: 300 }}
          htmlAttributes={{
            alt: displayName ?? 'Special offer',
            onLoad: () => setImageLoaded(true),
          }}
          $loaded={imageLoaded}
        />
      </ImageContainer>
    </Container>
  );
}

const skeletonPulse = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
`;

const Container = styled.div<{ $showExpiredSpecial: boolean }>`
  width: 100%;
  ${({ $showExpiredSpecial }) =>
    $showExpiredSpecial &&
    `
    filter: grayscale(80%) opacity(0.75);
  `}
  margin-bottom: 8px;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 16px 0;
`;

const Title = styled.h2`
  font-weight: 700;
  font-size: 20px;
  line-height: 1.2;
  color: var(--dcm-color-text-primary, ${({ theme }) => theme.colors.primaryBlack});
`;

const Description = styled.p`
  font-size: 16px;
  line-height: 1.4;
  color: var(--dcm-color-text-secondary, #485055);
  word-break: break-word;
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 150px;
  border-radius: 16px;
  overflow: hidden;
`;

const ImageSkeleton = styled.div`
  position: absolute;
  inset: 0;
  animation: ${skeletonPulse} 1s linear infinite;
  background-color: ${({ theme }) => theme.colors?.grey?.[95] ?? '#e3e7e9'};
  border-radius: 16px;
`;

const BannerImage = styled(Imgix)<{ $loaded: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  opacity: ${({ $loaded }) => ($loaded ? 1 : 0)};
  transition: opacity 0.2s ease-in;
`;
